import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { InMemoryCache } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { AccountsClient, AuthClient, BaseClient, DataClient, EntitiesClient, HealthClient, MapClient, MediaClient, ReportsClient, RequestCache, RequestOptions, SearchClient, StatsClient, TasksClient } from '@key-telematics/fleet-api-client';
import { Throttle } from '@key-telematics/fleet-api-client/lib/throttle';
import { Apollo } from 'apollo-angular';
import moment from 'moment';
import TinyCache from 'tinycache';
import { createEventFilterRequestMask, entityWithEventFilterResponseMask, eventFilterListResponseMask, eventFilterResponseMask, eventListResponseMask, updateEventFilterRequestMask, createEntityWithEventFilterRequestMask, updateEntityWithEventFilterRequestMask, eventResponseMask } from './api.masker';

export interface EnvironmentVariables {
    apiEndpoint: string;
}

export class TinyRequestCache implements RequestCache {

    private cache = new TinyCache();

    async get<T>(key: string): Promise<T> {
        return this.cache.get(key);
    }

    async put<T>(key: string, timeout: number, value: T): Promise<void> {
        this.cache.put(key, value, timeout * 1000);
    }

}



/** The ApiService wraps and configures the Fleet API clients. */
@Injectable()
export class ApiService {


    endpoint: string;
    accessToken: string;

    private requestCache = new TinyRequestCache();

    throttle: Throttle = new Throttle([
        {
            requestsPerSecond: 1,
            concurrency: 5,
            methods: ['POST', 'PUT', 'DELETE'],
        },
        {
            requestsPerSecond: 5,
            concurrency: 5,
        },
    ])

    auth = new AuthClient({ throttle: this.throttle });
    accounts = new AccountsClient({ throttle: this.throttle });
    entities = (() => {
        const client = new EntitiesClient({ throttle: this.throttle });

        client.listAlerts = eventFilterListResponseMask(client, client.listAlerts);
        client.getAlert = eventFilterResponseMask(client, client.getAlert);
        client.createAlert = createEventFilterRequestMask(client, client.createAlert);
        client.updateAlert = updateEventFilterRequestMask(client, client.updateAlert);

        client.getAssetRatingProfile = entityWithEventFilterResponseMask(client, client.getAssetRatingProfile);
        client.createAssetRatingProfile = createEntityWithEventFilterRequestMask(client, client.createAssetRatingProfile);
        client.updateAssetRatingProfile = updateEntityWithEventFilterRequestMask(client, client.updateAssetRatingProfile);

        client.getAssetStateProfile = entityWithEventFilterResponseMask(client, client.getAssetStateProfile);
        client.createAssetStateProfile = createEntityWithEventFilterRequestMask(client, client.createAssetStateProfile);
        client.updateAssetStateProfile = updateEntityWithEventFilterRequestMask(client, client.updateAssetStateProfile);

        return client;
    })();
    tasks = new TasksClient({ throttle: this.throttle });
    reports = new ReportsClient({ throttle: this.throttle });
    data = (() => {
        const client = new DataClient({ throttle: this.throttle });

        client.getEventFeed = eventListResponseMask(client, client.getEventFeed);
        client.getEventHistory = eventListResponseMask(client, client.getEventHistory);

        client.getAlertFeed = eventListResponseMask(client, client.getAlertFeed);
        client.getAlertHistory = eventListResponseMask(client, client.getAlertHistory);

        return client;
    })();
    search = new SearchClient({ throttle: this.throttle });
    map = new MapClient({ throttle: this.throttle });
    health = new HealthClient({ throttle: this.throttle });
    stats = new StatsClient({ throttle: this.throttle });
    media = (() => {
        const client = new MediaClient({ throttle: this.throttle });

        client.listMedia = eventListResponseMask(client, client.listMedia);
        client.getMedia = eventResponseMask(client, client.getMedia);
        client.getMediaInfo = eventResponseMask(client, client.getMediaInfo);
        client.saveMedia = eventResponseMask(client, client.saveMedia);
        client.cancelMedia = eventResponseMask(client, client.cancelMedia)
        client.deleteMediaFile = eventResponseMask(client, client.deleteMediaFile);

        return client;
    })();

    gql: Apollo;
    gqlEnabled = false;

    websocketConnected: boolean;

    private websocketClient: any;

    onTokenExpired: EventEmitter<Error> = new EventEmitter();
    onTokenRefreshed: EventEmitter<string> = new EventEmitter();

    constructor(private zone: NgZone, public http: HttpClient) {
        this.clients().forEach(service => {
            // Enable below if you want to log all api requests
            // service.logger = {
            //     log: (line: string) => {
            //         console.log(line);
            //     },
            // };
            service._addErrorHandler(this.apiErrorHandler.bind(this));
        });
    }

    private clients(): BaseClient[] {
        return [this.auth, this.accounts, this.entities, this.data, this.reports, this.search, this.tasks, this.media, this.map, this.health, this.stats];
    }

    private apiErrorHandler(err: any, req: any) {
        if (err && err.name) {
            console.warn(req && req.req ? req.req.url : null, err);
        }
        if (err && err.name && ['TokenExpiredError', 'OTPRequiredError'].includes(err.name)) {
            this.onTokenExpired.emit(err);
        }
    }

    get isStagingEndpoint(): boolean {
        return this.endpoint && (
            this.endpoint.includes('127.0.0.1') ||
            this.endpoint.includes('localhost') ||
            this.endpoint.includes('staging')
        );
    }

    setEndpoint(endpoint: string) {
        this.endpoint = endpoint;
        this.clients().forEach(client => {
            client.url = endpoint;
        });
    }

    setAccessToken(token: string, refresh: boolean = false) {
        this.accessToken = token;
        this.clients().forEach(client => {
            client.setAccessToken(token);
        });
        this.onTokenRefreshed.emit(token);

        if (this.gqlEnabled && this.endpoint && !refresh) { // do not recreate the websocket on token refreshes!
            this.gql = this.createApolloClient();
        }
    }

    createApolloClient(): Apollo {

        if (this.websocketClient) {
            this.websocketClient.dispose();
            this.websocketClient = null;
            this.websocketConnected = false;
        }

        const debug = (...args) => {
            if (this.endpoint.includes('staging') || this.endpoint.includes('localhost')) {
                console.log(`[${new Date().toISOString()}] WEBSOCKET`, ...args);
            }
        };

        let connectTime;

        this.websocketClient = createClient({
            url: this.endpoint.replace('http://', 'ws://').replace('https://', 'wss://') + '/gql',
            connectionParams: async () => ({
                headers: {
                    'x-access-token': this.accessToken,
                },
            }),
            retryAttempts: 10000,
            shouldRetry: () => true,
            on: {
                connected: (...args) => {
                    debug('Connected', ...args);
                    this.websocketConnected = true;
                    connectTime = moment();
                },
                closed: (...args) => {
                    const time = moment().diff(connectTime, 'seconds');
                    debug('Disconnected', time, ...args);
                    this.websocketConnected = false;
                },
                error: (...args) => {
                    debug('Error', ...args);
                },
            },
        });

        const ws = new GraphQLWsLink(this.websocketClient);

        // if we ever want to do HTTP graphql, here's the needed link
        // gqlHeaders: HttpHeaders = new HttpHeaders();
        // this.gqlHeaders = this.gqlHeaders.set('x-access-token', token);
        // const http = new HttpLink(this.http).create({
        //     headers: this.gqlHeaders,
        //     uri: this.endpoint + '/gql',
        // });

        return new Apollo(this.zone, {
            cache: new InMemoryCache(),
            link: ws,
        });

    }

    cacheFor(minutes: number): RequestOptions {
        return {
            cache: this.requestCache,
            cacheTimeout: minutes * 60,
        };
    }


}

/** This will return null if the error is an Access Denied error */
export function nullAccessDenied(error: Error) {
    if (error.name === 'ForbiddenError') {
        return Promise.resolve(null);
    }
    throw error;
}

/** This will return null if the error is a 404 not found error */
export function nullNotFound(error: Error) {
    if (error.name === 'NotFoundError') {
        return Promise.resolve(null);
    }
    throw error;
}
